import { Stack } from '@fluentui/react';

import { ResourceType } from '../constants/resource';
import { TokenUsageStats, UsageStats } from '../types';
import { StatCard } from './StatCard';

interface MetricsCardsProps {
  stats: UsageStats;
  tokenStats?: TokenUsageStats;
  resourceType: ResourceType;
  isLoading: boolean;
  granularityLabel: string;
  groupByLabel: string;
}

/**
 * Format large numbers with K/M suffix for better readability
 */
function formatTokenCount(value: number): string {
  if (value >= 1_000_000) {
    return `${(value / 1_000_000).toFixed(2)}M`;
  }
  if (value >= 1_000) {
    return `${(value / 1_000).toFixed(2)}K`;
  }
  return value.toLocaleString();
}

/**
 * MetricsCards component - Displays key usage statistics in card format
 *
 * For AI Units shows:
 * - Maximum AI units consumed in a single period
 * - Average AI units per period
 * - Count of unique groups (workspaces, users, etc.)
 *
 * For H2OGPTe Tokens shows:
 * - Total tokens consumed
 * - Input tokens
 * - Output tokens
 */
export function MetricsCards({
  stats,
  tokenStats,
  resourceType,
  isLoading,
  granularityLabel,
  groupByLabel,
}: MetricsCardsProps) {
  // H2OGPTe Token stats
  if (resourceType === ResourceType.H2OGPTE_TOKENS && tokenStats) {
    return (
      <Stack horizontal tokens={{ childrenGap: 16 }} styles={{ root: { marginBottom: 16 } }}>
        <StatCard title="Total Tokens" value={formatTokenCount(tokenStats.totalTokens)} isLoading={isLoading} />
        <StatCard title="Input Tokens" value={formatTokenCount(tokenStats.inputTokens)} isLoading={isLoading} />
        <StatCard title="Output Tokens" value={formatTokenCount(tokenStats.outputTokens)} isLoading={isLoading} />
        <StatCard title={`${groupByLabel} Count`} value={tokenStats.uniqueCount} isLoading={isLoading} />
      </Stack>
    );
  }

  // AI Units stats (default)
  return (
    <Stack horizontal tokens={{ childrenGap: 16 }} styles={{ root: { marginBottom: 16 } }}>
      <StatCard title="Max AI Units" value={stats.maxAiUnits.toFixed(2)} isLoading={isLoading} />
      <StatCard
        title={`Average ${granularityLabel} AI Units`}
        value={stats.averageAiUnits.toFixed(2)}
        isLoading={isLoading}
      />
      <StatCard title={`${groupByLabel} Count`} value={stats.uniqueCount} isLoading={isLoading} />
    </Stack>
  );
}
